import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { PrettoSlider } from '../../../assets/styles/styles';
import { CompanyService } from '../../../service/CompanyService';
import { CompanySpacesService } from '../../../service/CompanySpacesService';
import { UserService } from '../../../service/UserService';
import { useAuthStore } from '../../../store/storeAuth';
import useFlowsStore from '../../../store/storeFlows';
import {
  LOCKED_TEMPERATURE_VALUE,
  findCommandTextModelCapabilities,
  getCommandTextReasoningLockedOn,
  getCommandTextReasoningVisible,
  getCommandTextTemperatureLocked,
  getCommandTextWebSearchVisible,
} from '../../../util/commandTextModelFeatures';
import {
  AiTextGeneratorFormValues,
  apiSystemToCanonical,
  canonicalSystemDisplayNameMap,
} from '../../AiConfigs/constants';
import KitModal from '../../UI/KitModal';
import Gemini from '../Gemini';
import GroqAi from '../GroqAi';
import OpenAi from '../OpenAi';
import OpenSources from '../OpenSources';
import XAi from '../XAi';

const PROVIDERS_WITH_CAPABILITIES = new Set([
  'openai',
  'anthropic',
  'gemini',
  'xai',
  'groq',
]);

type Props = {
  onHide: () => void;
  show: boolean;
  formData: AiTextGeneratorFormValues;
  onChange: (e: any) => void;
  nodeData?: any;
};

const SettingsModal = ({
  onHide,
  show,
  formData,
  onChange,
  nodeData,
}: Props) => {
  const {
    user,
    technologies,
    companySpaceTechnologies,
    setCompanySpaceTechnologies,
    setTechnologies,
  } = useAuthStore(state => state);
  const { spaceId } = useFlowsStore();
  const [anthropicAutoSettings, setAnthropicAutoSettings] = useState(
    formData.autoToken,
  );
  const [perplexityAutoSettings, setPerplexityAutoSettings] = useState(
    formData.autoToken,
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    const fetchAllTechnologies = async () => {
      try {
        if (spaceId) {
          const res = await CompanySpacesService.getSpaceTechnologies(
            spaceId as string,
          );
          setCompanySpaceTechnologies(res.data);
        }

        if (technologies.length === 0) {
          const technologiesData = user?.selectedCompanyId
            ? await CompanyService.getCompanyTechnologies(
                user.selectedCompanyId,
              )
            : await UserService.getTechnologies();
          setTechnologies(technologiesData.data);
        }
      } catch (error) {
        console.log('Error fetching technologies:', error);
      }
    };

    fetchAllTechnologies();
  }, [spaceId, show, user?.selectedCompanyId]);

  const textSystems = [
    'anthropic',
    'openai',
    'groq',
    'gemini',
    'perplexity',
    'opensources',
    'xai',
  ];

  const filteredTextSystems = (() => {
    const systemsToUse =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    return (
      systemsToUse
        ?.filter(system => textSystems?.includes(system?.system))
        ?.map(system => ({
          ...system,
          models: system?.models?.filter(
            (model: any) => model.type === 'Text' && !model.isDeprecated,
          ),
        })) || []
    );
  })();

  const commandTextCapabilityUi = useMemo(() => {
    if (!PROVIDERS_WITH_CAPABILITIES.has(formData.aiSystem)) {
      return null;
    }
    if (!formData.aiModel) {
      return null;
    }
    const capabilities = findCommandTextModelCapabilities(
      filteredTextSystems,
      formData.aiSystem,
      formData.aiModel,
    );
    if (!capabilities) {
      return null;
    }
    return {
      showReasoning: getCommandTextReasoningVisible(capabilities),
      reasoningLockedOn: getCommandTextReasoningLockedOn(capabilities),
      showWebSearch: getCommandTextWebSearchVisible(capabilities),
      temperatureLocked: getCommandTextTemperatureLocked(capabilities),
    };
  }, [filteredTextSystems, formData.aiSystem, formData.aiModel]);

  useEffect(() => {
    if (
      commandTextCapabilityUi?.showReasoning &&
      commandTextCapabilityUi.reasoningLockedOn &&
      formData.reasoning !== true
    ) {
      onChange({ target: { name: 'reasoning', value: true } });
      return;
    }

    if (
      commandTextCapabilityUi &&
      !commandTextCapabilityUi.showReasoning &&
      formData.reasoning === true
    ) {
      onChange({ target: { name: 'reasoning', value: false } });
    }
  }, [
    commandTextCapabilityUi?.showReasoning,
    commandTextCapabilityUi?.reasoningLockedOn,
    formData.reasoning,
  ]);

  useEffect(() => {
    if (
      commandTextCapabilityUi?.temperatureLocked &&
      Number(formData.anthropicTemperature) !== LOCKED_TEMPERATURE_VALUE
    ) {
      onChange({
        target: {
          name: 'anthropicTemperature',
          value: LOCKED_TEMPERATURE_VALUE,
        },
      });
    }
  }, [
    commandTextCapabilityUi?.temperatureLocked,
    formData.anthropicTemperature,
  ]);

  useEffect(() => {
    if (
      commandTextCapabilityUi &&
      !commandTextCapabilityUi.showWebSearch &&
      formData.isWebEnabled === true
    ) {
      onChange({ target: { name: 'isWebEnabled', value: false } });
    }
  }, [commandTextCapabilityUi?.showWebSearch, formData.isWebEnabled]);

  // NOTE: previous in-modal migration of deprecated models has been removed.
  // After the SQL Phase 1 canonicalization, `aiModel` already holds the exact
  // SDK id and there is no longer a per-provider field to migrate. If a model
  // becomes deprecated server-side, the dropdown simply won't list it; the
  // user can pick a new one.

  return (
    <KitModal
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '650px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      onHide={onHide}
      show={show}
    >
      <Box>
        <Typography
          variant='h6'
          color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
        >
          Advanced Settings
        </Typography>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          mt={'16px'}
          p={'8px'}
          borderRadius={'4px'}
        >
          <Typography
            fontSize={'14px'}
            variant='h6'
            color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
          >
            Prompt Input
          </Typography>

          <FormControl sx={{ width: '100%' }}>
            <InputLabel id='demo-simple-select-label'>Ai System</InputLabel>
            <Select
              name='aiSystem'
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              label='Ai System'
              value={formData.aiSystem}
              onChange={onChange}
              sx={{
                width: '100%',
                '& .MuiList-root': {
                  backgroundColor:
                    user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                },
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                border: `2px solid ${
                  user?.theme === 'dark' ? '#475467' : '#EAECF0'
                }`,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                    },
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                },
              }}
            >
              {filteredTextSystems?.map((opt, index) => {
                const disabled = opt?.models?.every(
                  (model: any) => model.enabled === false,
                );
                const disabledByCompany = opt?.models?.every(
                  (model: any) => model.disabledByCompany,
                );
                const canonicalValue =
                  apiSystemToCanonical[opt.system] || opt.system;
                const displayName =
                  canonicalSystemDisplayNameMap[
                    canonicalValue as keyof typeof canonicalSystemDisplayNameMap
                  ] || canonicalValue;
                return (
                  <MenuItem
                    key={index}
                    value={canonicalValue}
                    disabled={disabled}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography>{displayName}</Typography>
                      {disabledByCompany && (
                        <Chip
                          label='Disabled by admin'
                          color='error'
                          size='small'
                          variant='filled'
                          sx={{
                            borderRadius: '8px',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {formData.aiSystem === 'openai' && (
            <OpenAi
              systems={filteredTextSystems}
              onChange={onChange}
              watch={formData}
              tokenCount={0}
            />
          )}

          {formData.aiSystem === 'groq' && (
            <GroqAi
              onChange={onChange}
              watch={formData}
              systems={filteredTextSystems}
            />
          )}

          {formData.aiSystem === 'gemini' && (
            <Gemini
              imageSystem
              watch={formData}
              onChange={onChange}
              systems={filteredTextSystems}
            />
          )}

          {formData.aiSystem === 'opensources' && (
            <OpenSources
              watch={formData}
              onChange={onChange}
              systems={filteredTextSystems}
            />
          )}

          {formData.aiSystem === 'perplexity' && (
            <>
              <Box>
                <FormLabel
                  sx={{
                    mb: '8px',
                    mt: '8px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Type
                </FormLabel>
                <Select
                  className='nodrag'
                  value={formData.aiModel}
                  fullWidth
                  size='small'
                  onChange={onChange}
                  name={'aiModel'}
                  sx={{
                    '& .MuiList-root': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                >
                  {filteredTextSystems
                    ?.filter((item: any) => item.system === 'perplexity')?.[0]
                    ?.models?.map((opt: any) => (
                      <MenuItem
                        key={opt.id}
                        value={opt.model}
                        disabled={opt.disabledByCompany}
                      >
                        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                          <Typography
                            variant='body2'
                            color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                          >
                            {opt.model}
                          </Typography>
                          {opt.disabledByCompany && (
                            <Typography fontSize={'12px'} color='red'>
                              (disabled by admin)
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </Box>

              <Box>
                <FormLabel
                  sx={{
                    mb: '8px',
                    mt: '8px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  Search Context Size
                </FormLabel>

                <Select
                  className='nodrag'
                  value={formData.search_context_size}
                  fullWidth
                  size='small'
                  onChange={onChange}
                  name={'search_context_size'}
                  sx={{
                    '& .MuiList-root': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                >
                  <MenuItem value='low'>
                    <Typography>Low</Typography>
                  </MenuItem>
                  <MenuItem value='medium'>
                    <Typography>Medium</Typography>
                  </MenuItem>
                  <MenuItem value='high'>
                    <Typography>High</Typography>
                  </MenuItem>
                </Select>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: '20px',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      onChange={e => {
                        onChange({
                          target: {
                            name: 'autoToken',
                            value: !formData.autoToken,
                          },
                        });

                        setPerplexityAutoSettings(!formData.autoToken);
                      }}
                      name='autoToken'
                      checked={formData.autoToken}
                      sx={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    />
                  }
                  label='Auto setting'
                />
                <FormControl>
                  <TextField
                    id='demo-simple-select'
                    type={'number'}
                    onChange={onChange}
                    value={
                      perplexityAutoSettings ? 0 : formData.perplexityTokens
                    }
                    disabled={perplexityAutoSettings}
                    className='nodrag'
                    label={'Output Token Max'}
                    name={'max_tokens'}
                    InputProps={{
                      style: {
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      },
                    }}
                    sx={{
                      width: '290px',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      border: `2px solid ${
                        user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                    }}
                  />
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                >
                  Top P
                </FormLabel>
                <PrettoSlider
                  step={0.1}
                  min={0}
                  name={'perplexityTopP'}
                  value={Number(formData.perplexityTopP)}
                  max={1}
                  style={{ marginLeft: '8px', width: '350px' }}
                  onChange={e => {
                    onChange(e);
                  }}
                  className='nodrag'
                />
                <TextField
                  type={'number'}
                  value={
                    formData[
                      'perplexityTopP' as keyof AiTextGeneratorFormValues
                    ]
                  }
                  name={'perplexityTopP'}
                  onChange={e => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  style={{ marginLeft: '8px', width: '80px' }}
                  className='nodrag'
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                >
                  Temperature (Creativity of LLM response)
                </FormLabel>
                <PrettoSlider
                  step={0.1}
                  min={0}
                  name={'perplexityTemperature'}
                  value={Number(formData.perplexityTemperature)}
                  max={2}
                  style={{ marginLeft: '8px', width: '350px' }}
                  onChange={e => {
                    onChange(e);
                  }}
                  className='nodrag'
                />
                <TextField
                  type={'number'}
                  value={
                    formData[
                      'perplexityTemperature' as keyof AiTextGeneratorFormValues
                    ]
                  }
                  name={'perplexityTemperature'}
                  onChange={e => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 1,
                      step: 0.1,
                    },
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  style={{ marginLeft: '8px', width: '80px' }}
                  className='nodrag'
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                >
                  Top K
                </FormLabel>
                <PrettoSlider
                  step={1}
                  min={0}
                  name={'perplexityTopK'}
                  value={Number(formData.perplexityTopK)}
                  max={40}
                  style={{ marginLeft: '8px', width: '350px' }}
                  onChange={e => {
                    onChange(e);
                  }}
                  className='nodrag'
                />
                <TextField
                  type={'number'}
                  value={
                    formData[
                      'perplexityTopK' as keyof AiTextGeneratorFormValues
                    ]
                  }
                  name={'perplexityTopK'}
                  onChange={e => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  style={{ marginLeft: '8px', width: '80px' }}
                  className='nodrag'
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </Box>
            </>
          )}

          {formData.aiSystem === 'anthropic' && (
            <>
              <Box mt={'4px'}>
                <Typography
                  fontSize={'14px'}
                  fontWeight={500}
                  sx={{
                    mb: '4px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                  variant='body1'
                >
                  Model
                </Typography>
                <FormControl sx={{ width: '100%' }}>
                  <Select
                    className='nodrag'
                    name='aiModel'
                    fullWidth
                    size='small'
                    value={formData.aiModel}
                    sx={{
                      height: 'unset',
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    }}
                    onChange={onChange}
                    MenuProps={{
                      style: {
                        maxHeight: '700px',
                      },
                    }}
                  >
                    {(
                      filteredTextSystems
                        ?.find((item: any) => item?.system === 'anthropic')
                        ?.models?.filter((m: any) => m.type === 'Text') || []
                    ).map((opt: any, index: number) => (
                      <MenuItem
                        className='nodrag'
                        key={opt.model + index}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'start',
                          alignItems: 'start',
                          padding: '16px',
                        }}
                        value={opt.model}
                        disabled={
                          opt.enabled === false || opt.disabledByCompany
                        }
                      >
                        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                          <Typography
                            mb={'4px'}
                            textTransform={'capitalize'}
                            variant='body2'
                            color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                          >
                            {opt.displayName || opt.model}
                          </Typography>
                          {opt.disabledByCompany && (
                            <Typography fontSize={'12px'} color='red'>
                              (disabled by admin)
                            </Typography>
                          )}
                        </Box>
                        <Box
                          display={'flex'}
                          gap={'8px'}
                          alignItems={'center'}
                          flexWrap={'wrap'}
                        >
                          {opt?.tags?.map((tag: any, index: number) => {
                            const tagLabel =
                              typeof tag === 'string' ? tag : tag.label;
                            const tagTooltip =
                              typeof tag === 'string' ? '' : tag.tooltip;

                            return (
                              <Tooltip
                                key={tagLabel + index}
                                title={tagTooltip}
                                arrow
                                placement='top'
                              >
                                <Chip
                                  sx={{
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    border: `1px solid ${
                                      user?.theme === 'dark'
                                        ? '#475467'
                                        : '#EAECF0'
                                    }`,
                                    color:
                                      user?.theme === 'dark'
                                        ? '#D0D5DD'
                                        : 'black',
                                  }}
                                  label={tagLabel}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                        <Typography
                          maxWidth={'570px'}
                          variant='body2'
                          sx={{
                            color: '#667085',
                            mt: '8px',
                            whiteSpace: 'pre-wrap',
                            textAlign: 'start',
                          }}
                        >
                          {opt.description}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: '20px',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      onChange={e => {
                        onChange({
                          target: {
                            name: 'autoToken',
                            value: !formData.autoToken,
                          },
                        });

                        setAnthropicAutoSettings(!formData.autoToken);
                      }}
                      name='autoToken'
                      checked={formData.autoToken}
                      sx={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    />
                  }
                  label='Auto setting'
                />
                <FormControl>
                  <TextField
                    id='demo-simple-select'
                    type={'number'}
                    onChange={onChange}
                    value={anthropicAutoSettings ? 0 : formData.anthropicToken}
                    disabled={anthropicAutoSettings}
                    className='nodrag'
                    label={'Output Token Max'}
                    name={'anthropicToken'}
                    InputProps={{
                      style: {
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      },
                    }}
                    sx={{
                      width: '290px',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      border: `2px solid ${
                        user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                    }}
                  />
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                >
                  Top K
                </FormLabel>
                <PrettoSlider
                  step={1}
                  min={1}
                  name={'anthropicTopK'}
                  value={Number(formData.anthropicTopK)}
                  max={40}
                  style={{ marginLeft: '8px', width: '350px' }}
                  onChange={e => {
                    onChange(e);
                  }}
                  className='nodrag'
                />
                <TextField
                  type={'number'}
                  value={
                    formData['anthropicTopK' as keyof AiTextGeneratorFormValues]
                  }
                  name={'anthropicTopK'}
                  onChange={e => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  style={{ marginLeft: '8px', width: '80px' }}
                  className='nodrag'
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                >
                  Top P
                </FormLabel>
                <PrettoSlider
                  step={1}
                  min={1}
                  name={'anthropicTopP'}
                  value={Number(formData.anthropicTopP)}
                  max={40}
                  style={{ marginLeft: '8px', width: '350px' }}
                  onChange={e => {
                    onChange(e);
                  }}
                  className='nodrag'
                />
                <TextField
                  type={'number'}
                  value={
                    formData['anthropicTopP' as keyof AiTextGeneratorFormValues]
                  }
                  name={'anthropicTopP'}
                  onChange={e => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 40,
                      step: 1,
                    },
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  style={{ marginLeft: '8px', width: '80px' }}
                  className='nodrag'
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FormLabel
                  sx={{
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                >
                  Temperature (Creativity of LLM response)
                </FormLabel>
                <PrettoSlider
                  step={0.1}
                  min={0}
                  name={'anthropicTemperature'}
                  value={
                    commandTextCapabilityUi?.temperatureLocked
                      ? LOCKED_TEMPERATURE_VALUE
                      : Number(formData.anthropicTemperature)
                  }
                  max={1}
                  disabled={commandTextCapabilityUi?.temperatureLocked}
                  style={{ marginLeft: '8px', width: '350px' }}
                  onChange={e => {
                    onChange(e);
                  }}
                  className='nodrag'
                />
                <TextField
                  type={'number'}
                  disabled={commandTextCapabilityUi?.temperatureLocked}
                  value={
                    commandTextCapabilityUi?.temperatureLocked
                      ? LOCKED_TEMPERATURE_VALUE
                      : formData[
                          'anthropicTemperature' as keyof AiTextGeneratorFormValues
                        ]
                  }
                  name={'anthropicTemperature'}
                  onChange={e => {
                    onChange(e);
                  }}
                  InputProps={{
                    inputProps: {
                      min: 0,
                      max: 1,
                      step: 0.1,
                    },
                    style: {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  style={{ marginLeft: '8px', width: '80px' }}
                  className='nodrag'
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                />
              </Box>
            </>
          )}

          {formData.aiSystem === 'xai' && (
            <XAi
              systems={filteredTextSystems}
              onChange={onChange}
              watch={formData}
              tokenCount={0}
            />
          )}

          {commandTextCapabilityUi && (
              <Box mt={'16px'}>
                <Typography
                  fontSize={'14px'}
                  variant='h6'
                  color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                >
                  Model features
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginTop: '8px',
                  }}
                >
                  {commandTextCapabilityUi.showReasoning && (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.reasoning === true}
                            disabled={commandTextCapabilityUi.reasoningLockedOn}
                            onChange={ev => {
                              onChange({
                                target: {
                                  name: 'reasoning',
                                  value: ev.target.checked,
                                },
                              });
                            }}
                            name='reasoning'
                          />
                        }
                        label='Reasoning'
                      />
                    </FormControl>
                  )}
                  {commandTextCapabilityUi.showWebSearch && (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isWebEnabled === true}
                            onChange={ev => {
                              onChange({
                                target: {
                                  name: 'isWebEnabled',
                                  value: ev.target.checked,
                                },
                              });
                            }}
                            name='isWebEnabled'
                          />
                        }
                        label='Web search'
                      />
                    </FormControl>
                  )}
                </Box>
              </Box>
            )}

          <Box mt={'16px'}>
            <Typography
              fontSize={'14px'}
              variant='h6'
              color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
            >
              Multimodal Settings
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                marginTop: '8px',
              }}
            >
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        nodeData?.isEmptyState
                          ? true
                          : formData.multimodalEnabled
                            ? true
                            : false
                      }
                      onChange={e => {
                        onChange({
                          target: {
                            name: 'multimodalEnabled',
                            value: e.target.checked,
                          },
                        });
                      }}
                      name='multimodalEnabled'
                    />
                  }
                  label='Enable Multimodal AI Models'
                />
              </FormControl>
            </Box>
          </Box>

          <Box mt={'16px'}>
            <Typography
              fontSize={'14px'}
              variant='h6'
              color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
            >
              Streaming Settings
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                marginTop: '8px',
              }}
            >
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.streamEnabled}
                      onChange={e => {
                        onChange({
                          target: {
                            name: 'streamEnabled',
                            value: e.target.checked,
                          },
                        });
                      }}
                      name='streamEnabled'
                    />
                  }
                  label='Stream Enabled'
                />
              </FormControl>

              <FormControl
                disabled={!formData.streamEnabled}
                sx={{ width: '100%' }}
              >
                <InputLabel id='stream-type-label'>Stream Type</InputLabel>

                <Select
                  name='streamType'
                  labelId='stream-type-label'
                  id='stream-type-select'
                  label='Stream Type'
                  value={formData.streamType}
                  onChange={onChange}
                  sx={{
                    width: '100%',
                    '& .MuiList-root': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    },
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    border: `2px solid ${
                      user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                >
                  <MenuItem value='chat'>Chat</MenuItem>
                  <MenuItem value='form'>Form</MenuItem>
                  <MenuItem value='api_callback'>API Callback</MenuItem>
                </Select>
              </FormControl>

              {formData.streamType === 'api_callback' && (
                <FormControl>
                  <TextField
                    name='apiCallbackUrl'
                    label='API Callback URL'
                    value={formData.apiCallbackUrl}
                    onChange={onChange}
                  />
                </FormControl>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </KitModal>
  );
};

export default SettingsModal;
